/**
 * Does the signed-out guest database hold study data worth warning about?
 *
 * Signing in replaces local data with the account's cloud deck, so the app warns
 * first and offers an export. That warning must only appear when there is genuinely
 * something to lose — showing it to someone who just opened the app and tapped
 * "sign in" would be noise that teaches users to dismiss it.
 *
 * Counts rows across user tables and stops at the first one found. Returns `false`
 * on any failure: a broken probe must not block sign-in, and the warning is an
 * informational courtesy rather than a safety mechanism.
 */
import type { AnySQLiteDatabase } from "@-/com.npmjs.expo-sqlite";

/** sqlite internals + Turso bookkeeping + drizzle's journal — not user study data. */
const INTERNAL_TABLE_RE = /^(?:sqlite_|turso_|__turso|__drizzle|__new_)/i;

/**
 * Tables a BRAND-NEW profile populates before the user has studied anything.
 *
 * `user` is the local profile row, written during actor-database bootstrap. Counting it
 * made this probe return `true` for someone who had done nothing at all, so the
 * replace-warning fired on every sign-in — precisely the noise the docblock above says
 * it must avoid, and it teaches users to dismiss the dialog unread.
 *
 * `collection` is deliberately NOT exempt. It used to be: the Favorites collection was
 * created eagerly at app start, so it existed for every profile. That eager write is
 * gone — `useEnsureFavoritesCollection` now only SELF-HEALS (duplicates from sync, stale
 * ownership after the sign-in bootstrap), and the row is created lazily by the first
 * "add to Favorites" tap. So a `collection` row again means the user actually did
 * something, and it should count as data worth warning about.
 *
 * `study_session` is study TIME, not study: the Learn layout records an interval for every focused
 * deck/lesson/slide route (`StudySessionBoundary`), so merely LOOKING at the onboarding welcome
 * slide writes one within seconds. Counting it fired the warning for a guest who had done
 * nothing; after a sign-out it blocked the next sign-in behind the dialog (Android
 * `study-survives-sign-out`). Anything actually studied (cards, reviews, notes, favorites) still
 * counts through its own tables, so time without study is not data worth warning about.
 */
const NON_STUDY_TABLE_SET = new Set(["user", "device_settings", "study_session"]);

/**
 * Per-table row filters for tables that mix bootstrap noise with real user data.
 *
 * `user_slide` holds BOTH slide-view completions and slide favorites. The intro
 * onboarding walk writes a `completed_at` row for every slide the user pages
 * through — so a guest who has merely finished onboarding (studied NOTHING) has
 * several `user_slide` rows. Counting those fired the sign-in replace-warning for
 * every onboarded guest, and because that dialog gates the auth request, Maestro's
 * web sign-in (which onboards first — web has no pre-onboarding auth gate) hung on
 * an unhandled dialog: submit tapped, no request fired, no session ever formed
 * (dev-CI `deck-prepopulated.yaml`, runs 30837007263 → 30904290710).
 *
 * A FAVORITED slide is a deliberate user action worth warning about, so those rows
 * still count. Same reasoning as the `user` exemption above: rows a brand-new
 * profile populates on its own must not trip the warning.
 */
const TABLE_ROW_FILTER_MAP = new Map<string, string>([
  ["user_slide", "favorited_at IS NOT NULL AND deleted_at IS NULL"],
]);

interface SqliteMasterRow {
  name: string;
}

interface CountRow {
  n: number;
}

export async function hasLocalStudyData(connection: AnySQLiteDatabase): Promise<boolean> {
  try {
    const tables = await connection.getAllAsync<SqliteMasterRow>(
      `SELECT name FROM sqlite_master WHERE type = 'table' ORDER BY name`,
    );
    for (const { name } of tables) {
      if (INTERNAL_TABLE_RE.test(name) || NON_STUDY_TABLE_SET.has(name.toLowerCase())) {
        continue;
      }
      const filter = TABLE_ROW_FILTER_MAP.get(name.toLowerCase());
      const row = await connection.getFirstAsync<CountRow>(
        `SELECT COUNT(*) AS n FROM "${name}"${filter === undefined ? "" : ` WHERE ${filter}`} LIMIT 1`,
      );
      if ((row?.n ?? 0) > 0) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}
